/**
 * PHASE0 structured logger — one-line `console.info` per key boundary
 * so dev-mode observation doesn't require a debugger.
 *
 * Format per the directive:
 *   `PHASE0[client] event=<name> details={...} duration_ms=...`
 *
 * Where `details` is either:
 *   - compact key=value pairs when the values are all primitive, or
 *   - a JSON blob when nested (string-escaped once; no newline noise).
 *
 * Detail level per CLIENT_DESIGN §18:
 *   - dev default: `info` — emit info / warn / error.
 *   - `?debug=verbose` URL flag: emit `debug` + `info` + warn + error.
 *   - production (Vite's `import.meta.env.PROD`): warn + error only.
 *
 * The level is resolved once at module load from `window.location.search`
 * so the fast path on every log call is a single comparison.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function resolveLevel(): LogLevel {
  // Cast — `import.meta.env.PROD` is Vite-provided at build time; in tests
  // it's undefined and we default to `info`.
  const isProd =
    typeof import.meta !== 'undefined' &&
    (import.meta.env as { PROD?: boolean } | undefined)?.PROD === true;
  if (isProd) return 'warn';
  if (typeof window === 'undefined') return 'info';
  const q = window.location.search;
  if (q.includes('debug=verbose')) return 'debug';
  return 'info';
}

const ACTIVE_LEVEL: LogLevel = resolveLevel();

export interface LogFields {
  [key: string]: string | number | bigint | boolean | null | undefined;
}

/**
 * Format a single PHASE0 line. Exported for unit testing; the runtime
 * log functions call this then hand the string to the matching
 * `console.*` method.
 */
export function formatPhase0(
  event: string,
  fields: LogFields,
  duration_ms?: number,
): string {
  const parts: string[] = [];
  parts.push(`PHASE0[client] event=${event}`);
  const detailSegments: string[] = [];
  for (const key of Object.keys(fields)) {
    const v = fields[key];
    if (v === undefined) continue;
    if (v === null) {
      detailSegments.push(`${key}=null`);
      continue;
    }
    const str =
      typeof v === 'bigint'
        ? v.toString()
        : typeof v === 'string'
          ? quoteIfNeeded(v)
          : String(v);
    detailSegments.push(`${key}=${str}`);
  }
  if (detailSegments.length > 0) {
    parts.push(`details={${detailSegments.join(' ')}}`);
  }
  if (duration_ms !== undefined && Number.isFinite(duration_ms)) {
    parts.push(`duration_ms=${duration_ms.toFixed(2)}`);
  }
  return parts.join(' ');
}

function quoteIfNeeded(s: string): string {
  // Quote when the string has whitespace or special separators that
  // would break the grep-friendly flat line.
  if (/[\s={}]/.test(s) || s.length === 0) return JSON.stringify(s);
  return s;
}

function emitAt(
  level: LogLevel,
  event: string,
  fields: LogFields = {},
  duration_ms?: number,
): void {
  if (LEVEL_RANK[level] < LEVEL_RANK[ACTIVE_LEVEL]) return;
  const line = formatPhase0(event, fields, duration_ms);
  switch (level) {
    case 'debug':
      // eslint-disable-next-line no-console
      console.debug(line);
      break;
    case 'info':
      // eslint-disable-next-line no-console
      console.info(line);
      break;
    case 'warn':
      // eslint-disable-next-line no-console
      console.warn(line);
      break;
    case 'error':
      // eslint-disable-next-line no-console
      console.error(line);
      break;
  }
}

/** Module-level logger surface. Imported as `phase0` by callers. */
export const phase0 = {
  debug: (event: string, fields?: LogFields, duration_ms?: number) =>
    emitAt('debug', event, fields, duration_ms),
  info: (event: string, fields?: LogFields, duration_ms?: number) =>
    emitAt('info', event, fields, duration_ms),
  warn: (event: string, fields?: LogFields, duration_ms?: number) =>
    emitAt('warn', event, fields, duration_ms),
  error: (event: string, fields?: LogFields, duration_ms?: number) =>
    emitAt('error', event, fields, duration_ms),
  /** Current log level resolved from env + URL; exported for tests. */
  level: ACTIVE_LEVEL,
};
